import { c1_state } from "./C1_runtimeContext";
import type BaseObject from "../../../src/models/BaseObject";
import type BaseModel from "../../../src/models/BaseModel";

type ChangeListener = (object: C1_BaseObject<any>, property?: string) => void;
export type BeforeChangeCallback = (object: C1_BaseObject<any>, property?: string) => void;

export class C1_ChangeEvent {
  private listeners = new Map<number, ChangeListener>();
  private nextId = 0;

  subscribe(listener: ChangeListener): number {
    const id = ++this.nextId;
    this.listeners.set(id, listener);
    return id;
  }

  unsubscribe(id: number) {
    this.listeners.delete(id);
  }

  raise(object: C1_BaseObject<any>, property?: string) {
    this.listeners.forEach((listener) => listener(object, property));
  }
}

export abstract class C1_BaseObject<T> {
  protected _data: T;

  /**
   * ESTADA domain object extended by this C1 facet.
   *
   * This is deliberately separate from `parent`: during the migration the
   * legacy C1 model tree still uses `parent`, while newly embedded facets are
   * owned by an ESTADA BaseObject. The legacy parent/id/data responsibilities
   * will disappear as the individual facets are migrated.
   */
  private _owner?: BaseObject;

  get owner(): BaseObject | undefined {
    return this._owner;
  }

  attachOwner(owner: BaseObject): void {
    this._owner = owner;
  }

  detachOwner(): void {
    this._owner = undefined;
  }

  get className(): string {
    return this.constructor.name;
  }

  /** Returns the C1 catalog language selected in the global application settings. */
  protected get language() {
    return c1_state.language;
  }

  constructor(data: any | undefined, owner?: BaseModel) {
    this._data = {
      ...data,
    } as T;
    if (owner) {
      this.attachOwner(owner);
    }
  }

  /**
   * Compatibility projection of the owning ESTADA object's ID.
   * A C1 facet has no independent identity and never persists this value.
   */
  get id(): string {
    return this.owner?.id ?? "";
  }

  get masterIndex(): number | undefined {
    return (this._data as T & { masterIndex?: number }).masterIndex;
  }

  setMasterIndex(masterIndex?: number) {
    this.owner?.beforeChange("c1.masterIndex");
    (this._data as T & { masterIndex?: number }).masterIndex = masterIndex;
    this.owner?.changed("c1.masterIndex");
  }

  get tag(): string {
    return this.owner?.tag ?? "";
  }

  set tag(tag: string) {
    if (this.owner) {
      this.owner.tag = tag || "";
    }
  }

  setTag(tag?: string) {
    if (this.owner) {
      this.owner.tag = tag || "";
    }
  }

  get data(): Readonly<T> {
    return this._data;
  }

  get parent(): C1_BaseObject<any> | undefined {
    return this.owner?.parent?.c1;
  }

  // get isPersistent(): boolean {
  //   return true;
  // }

  // static setBeforeChangeCallback(callback?: BeforeChangeCallback) {
  //   C1_BaseObject.beforeChangeCallback = callback;
  // }

  protected changed(property?: string) {
    this.owner?.changed(property ? `c1.${property}` : "c1");
  }

  protected raiseOnChange(property?: string) {
    this.changed(property);
  }

  protected beforeChange(property?: string) {
    this.owner?.beforeChange(property ? `c1.${property}` : "c1");
  }

  // protected childChanged(object: C1_BaseObject<any>, property?: string) {
  //   this.onChange.raise(object, property);
  //   this.parent?.childChanged(object, property);
  // }

  toJSON(): unknown {
    return this._data;
  }

  fromJSON(input: unknown): void {
    this._data = { ...(input as T) };
  }
}
